import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, MapPin, Building2, ShieldCheck, FileText } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AppContext";
import { GridBg } from "../../components/shared/GridBg";
import { Logo } from "../../components/shared/Logo";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { FileUpload } from "../../components/shared/FileUpload";
import { CITIES } from "../../data/mockData";
import { toast } from "sonner";

const STEPS = [
  { key: "business", label: "Business", icon: Building2 },
  { key: "kyc",      label: "KYC",      icon: ShieldCheck },
  { key: "gst",      label: "GST",      icon: FileText },
  { key: "location", label: "Location", icon: MapPin },
];

export default function CompanyOnboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    company_name: user?.name || "", business_type: "", industry: "", website: "",
    contact_email: user?.email || "", contact_phone: "",
    authorized_person: "", kyc_file: null, company_doc_file: null,
    gstin: "", gst_legal_name: "", gst_certificate_file: null,
    address: "", city: "", pin: "",
  });

  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const progress = useMemo(() => Math.round(((step + 1) / STEPS.length) * 100), [step]);

  const canNext = () => {
    if (step === 0) return f.company_name && f.contact_email;
    if (step === 2) return true;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        company_name: f.company_name, business_type: f.business_type, industry: f.industry, website: f.website,
        contact_email: f.contact_email, contact_phone: f.contact_phone,
        authorized_person: f.authorized_person,
        kyc_file_id: f.kyc_file?.file_id || null,
        company_doc_file_id: f.company_doc_file?.file_id || null,
        gstin: f.gstin, gst_legal_name: f.gst_legal_name,
        gst_certificate_file_id: f.gst_certificate_file?.file_id || null,
        address: f.address, city: f.city, pin: f.pin,
      };
      await api.post("/profile/company", payload);
      await refresh();
      toast.success("Submitted for admin review", { description: "You'll be notified once verification completes." });
      navigate("/company", { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save profile"); }
    finally { setSaving(false); }
  };

  return (
    <GridBg className="min-h-screen">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-10">
          <Logo/>
          <div className="text-xs text-zinc-500">Verification · <span className="text-blue-400 font-mono">{progress}% complete</span></div>
        </div>

        <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2">
          {STEPS.map((s, i) => {
            const done = i < step, active = i === step;
            const Icon = s.icon;
            return (
              <React.Fragment key={s.key}>
                <div className="flex items-center gap-2 shrink-0" data-testid={`c-step-${s.key}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs"
                       style={{
                         background: done ? "#10B981" : active ? "rgba(59,130,246,0.15)" : "#111114",
                         border: `1px solid ${done ? "#10B981" : active ? "#3B82F6" : "#27272A"}`,
                         color: done ? "#0A0A0C" : active ? "#60A5FA" : "#71717A",
                       }}>
                    {done ? <Check size={12}/> : <Icon size={13}/>}
                  </div>
                  <span className={`text-xs uppercase tracking-widest ${active ? "text-white" : done ? "text-zinc-400" : "text-zinc-600"}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className="flex-1 h-px" style={{ background: done ? "#10B981" : "#27272A" }}/>}
              </React.Fragment>
            );
          })}
        </div>

        <div className="card-surface p-8 max-w-3xl">
          {step === 0 && (
            <Panel title="Business details" desc="How your company presents on Assisto4U.">
              <Row2>
                <Field label="Company name"><input data-testid="co-name" value={f.company_name} onChange={e => set("company_name", e.target.value)} className={ic}/></Field>
                <Field label="Business type">
                  <select data-testid="co-type" value={f.business_type} onChange={e => set("business_type", e.target.value)} className={ic + " appearance-none"}>
                    <option value="">Select…</option>
                    <option>Private Limited</option><option>LLP</option><option>Proprietorship</option><option>Partnership</option><option>Public Limited</option>
                  </select>
                </Field>
                <Field label="Industry">
                  <select data-testid="co-industry" value={f.industry} onChange={e => set("industry", e.target.value)} className={ic + " appearance-none"}>
                    <option value="">Select…</option>
                    <option>Manufacturing</option><option>Retail</option><option>Financial services</option><option>Legal & professional</option><option>Consumer goods</option><option>Consulting</option>
                  </select>
                </Field>
                <Field label="Website"><input data-testid="co-website" value={f.website} onChange={e => set("website", e.target.value)} placeholder="https://" className={ic}/></Field>
                <Field label="Contact email"><input data-testid="co-email" value={f.contact_email} onChange={e => set("contact_email", e.target.value)} className={ic} disabled/></Field>
                <Field label="Contact phone"><input data-testid="co-phone" value={f.contact_phone} onChange={e => set("contact_phone", e.target.value)} placeholder="+91 22 xxxxxxx" className={ic}/></Field>
              </Row2>
            </Panel>
          )}

          {step === 1 && (
            <Panel title="KYC of authorized person" desc="We verify a signatory before your company can post tasks.">
              <Field label="Authorized person name"><input data-testid="co-auth" value={f.authorized_person} onChange={e => set("authorized_person", e.target.value)} className={ic}/></Field>
              <FileUpload label="Identity document (Aadhaar / PAN)" testId="co-kyc-upload" purpose="company_kyc"
                          value={f.kyc_file} onChange={v => set("kyc_file", v)}
                          hint="Upload authorized person's ID"/>
              <FileUpload label="Company incorporation certificate" testId="co-doc-upload" purpose="company_doc"
                          value={f.company_doc_file} onChange={v => set("company_doc_file", v)}
                          hint="Upload incorporation certificate"/>
            </Panel>
          )}

          {step === 2 && (
            <Panel title="GST verification" desc="Enter GSTIN details — legal name must match GSTN records.">
              <Row2>
                <Field label="GSTIN"><input data-testid="co-gstin" value={f.gstin} onChange={e => set("gstin", e.target.value.toUpperCase())} placeholder="27AAAAA0000A1Z5" className={ic + " font-mono"}/></Field>
                <Field label="GST legal name"><input data-testid="co-gstname" value={f.gst_legal_name} onChange={e => set("gst_legal_name", e.target.value)} className={ic}/></Field>
              </Row2>
              <FileUpload label="GST certificate upload" testId="co-gst-upload" purpose="gst_certificate"
                          value={f.gst_certificate_file} onChange={v => set("gst_certificate_file", v)}
                          hint="Upload GST registration certificate"/>
            </Panel>
          )}

          {step === 3 && (
            <Panel title="Business location" desc="Primary office address — used for on-site task matching.">
              <Row2>
                <Field label="Address" className="sm:col-span-2"><input data-testid="co-address" value={f.address} onChange={e => set("address", e.target.value)} placeholder="Building, street, area" className={ic}/></Field>
                <Field label="City">
                  <select data-testid="co-city" value={f.city} onChange={e => set("city", e.target.value)} className={ic + " appearance-none"}>
                    <option value="">Select…</option>{CITIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="PIN"><input data-testid="co-pin" value={f.pin} onChange={e => set("pin", e.target.value)} placeholder="400001" className={ic}/></Field>
              </Row2>
              <div className="rounded-md overflow-hidden aspect-[16/6] relative border mt-2" style={{ borderColor: "var(--border)", background: "linear-gradient(135deg,#0f172a,#0b1220)" }}>
                <div className="absolute inset-0 grid-bg-fine opacity-70"/>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center pulse-dot" style={{ background: "rgba(59,130,246,0.2)", border: "2px solid #3B82F6" }}>
                    <MapPin size={14} className="text-blue-300"/>
                  </div>
                </div>
                <div className="absolute bottom-3 left-3 glass rounded-md p-2 text-[11px] text-zinc-300 font-mono">
                  {f.city || "—"} · {f.pin || "—"}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <StatusBadge status="Pending"/>
                <span className="text-xs text-zinc-500">On submit, your company goes into admin review</span>
              </div>
            </Panel>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t" style={{ borderColor: "var(--border)" }}>
            <button data-testid="co-back-btn" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}
                    className="btn-ghost px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5 disabled:opacity-40">
              <ArrowLeft size={12}/> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button data-testid="co-next-btn" onClick={() => canNext() && setStep(s => Math.min(STEPS.length - 1, s + 1))}
                      className="btn-primary px-5 py-2 rounded-full text-xs inline-flex items-center gap-1.5" disabled={!canNext()}>
                Continue <ArrowRight size={12}/>
              </button>
            ) : (
              <button data-testid="co-submit-btn" onClick={submit} disabled={saving}
                      className="btn-primary px-5 py-2 rounded-full text-xs inline-flex items-center gap-1.5">
                {saving ? "Saving…" : "Submit for review"} <Check size={12}/>
              </button>
            )}
          </div>
        </div>
      </div>
    </GridBg>
  );
}

const ic = "w-full px-3 py-2.5 rounded-md border bg-[#111114] text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-200 focus:border-blue-500/50 border-[#27272A] disabled:opacity-60";
const Panel = ({ title, desc, children }) => (
  <div>
    <h2 className="font-display text-2xl text-white">{title}</h2>
    <p className="text-sm text-zinc-500 mt-1.5">{desc}</p>
    <div className="mt-6 space-y-4">{children}</div>
  </div>
);
const Row2 = ({ children }) => <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
